import React, { useCallback, useEffect, useState, useMemo } from "react";
import MaterialReactTable, { type MRT_ColumnDef, MRT_ToggleFiltersButton } from "material-react-table";
import {
  FolderFilled,
  DocumentTextRegular,
  ArrowDownloadRegular,
  FolderAddRegular,
  DocumentAddRegular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import {
  Button,
  Input,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from "@fluentui/react-components";
import { MoreVerticalRegular } from "@fluentui/react-icons";
import { formatedDate } from "../../../../utils";
import apiServices from "../../../../service";
import DynamicDatePicker from "../../../../components/ControlledDatePicker/dynamicDatepicker";
import "../style.css";
import ReactQuillComponent from "../../../../components/ReactQuillComponent";

interface FileItem {
  type: "file";
  name: string;
  path: string;
  lastModified: string;
  metadata: any;
}

interface FolderItem {
  type: "folder";
  name: string;
  path: string;
  lastModified: string;
  children: (FolderItem | FileItem)[];
}

type Item = FolderItem | FileItem;

interface DocumentListProps {
  structure: Item[];
  searchResults: any[];
  search: string;
  setSearch: (value: string) => void;
  onAddFolder: () => void;
  onAddDoc: () => void;
  navigateTo: (path: string[], isBreadcrumb?: boolean) => void;
  currentPath: string[];
  activeCustomerId: string;
  loading: boolean;
  fileList: any;
}

// Date range filter component for the Date column
function makeDateFilterFormik(
  value: Date | null,
  onSet: (d: Date | null) => void,
) {
  return {
    values: { date: value },
    errors: {},
    touched: {},
    setFieldValue: (_name: string, val: Date | null) => onSet(val),
    setFieldError: () => { },
  };
}

function DateRangeFilter({ column }: { column: any }) {
  const [from, to] =
    (column.getFilterValue() as [Date | null, Date | null]) ?? [null, null];

  const fromFormik = makeDateFilterFormik(from, (val) =>
    column.setFilterValue((old: [Date | null, Date | null]) => [
      val,
      old?.[1] ?? null,
    ]),
  );
  const toFormik = makeDateFilterFormik(to, (val) =>
    column.setFilterValue((old: [Date | null, Date | null]) => [
      old?.[0] ?? null,
      val,
    ]),
  );

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <DynamicDatePicker formik={fromFormik} border name="date" width="140px" />
      <span style={{ fontSize: 12, color: "#888" }}>–</span>
      <DynamicDatePicker formik={toFormik} border name="date" width="140px" />
    </div>
  );
}

// Strip numeric timestamp prefix from file names (e.g. "1758120407923_filename.pdf")
function formatFileName(name: string): string {
  const match = name.match(/^\d+_(.+)$/);
  return match ? match[1] : name;
}

function ActionsCell({
  item,
  activeCustomerId,
}: {
  item: Item;
  activeCustomerId: string;
}) {
  if (item.type !== "file") return <span />;
  const handleDownload = () => {
    apiServices.document
      .getDocument(item.metadata?.documentid, activeCustomerId)
      .then(async (res: any) => {
        const data = res.data?.data;
        if (data) {
          const { fileUrl, DocumentTitle } = data;
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = DocumentTitle;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch((err: any) => console.error(err));
  };

  return (
    <Menu positioning="below-end">
      <MenuTrigger>
        <Button appearance="subtle" icon={<MoreVerticalRegular />} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<ArrowDownloadRegular />} onClick={handleDownload}>
            Download
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

const PAGE_SIZE = 20;

const DocumentList: React.FC<DocumentListProps> = ({
  structure,
  searchResults,
  search,
  setSearch,
  onAddFolder,
  onAddDoc,
  navigateTo,
  currentPath,
  activeCustomerId,
  loading,
  fileList,
}) => {
  const allItems: Item[] = search ? searchResults : structure;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [structure, searchResults, search]);

  const items = useMemo(() => allItems.slice(0, visibleCount), [allItems, visibleCount]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && visibleCount < allItems.length) {
      setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, allItems.length));
    }
  }, [visibleCount, allItems.length]);

  const columns = useMemo<MRT_ColumnDef<Item>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 240,
        Cell: ({ row }) => {
          const item = row.original;
          const displayName =
            item.type === "file" ? formatFileName(item.name) : item.name;

          const handleFolderClick = () => {
            if (search) {
              navigateTo(item.path.split("/").filter(Boolean));
            } else {
              navigateTo([...currentPath, item.name]);
            }
          };

          return (
            <div
              className={`doc-name-cell${item.type === "folder" ? " doc-folder-clickable" : ""}`}
              onClick={item.type === "folder" ? handleFolderClick : undefined}
              title={displayName}
            >
              {item.type === "folder" ? (
                <FolderFilled primaryFill="#fccc77" fontSize="20px" />
              ) : (
                <DocumentTextRegular primaryFill="#8c8c8c" fontSize="20px" />
              )}
              <span className="doc-item-name">{displayName}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 80,
        Cell: ({ row }) =>
          row.original.type === "folder" ? "Folder" : "File",
      },
      {
        accessorKey: "lastModified",
        header: "Date",
        size: 120,
        filterFn: (row, columnId, filterValue: [Date | null, Date | null]) => {
          const [from, to] = filterValue ?? [null, null];
          const cellValue = row.getValue<string>(columnId);
          if (!cellValue) return true;
          const cellDate = new Date(cellValue);
          cellDate.setHours(0, 0, 0, 0);
          if (from) {
            const fromDate = new Date(from);
            fromDate.setHours(0, 0, 0, 0);
            if (cellDate < fromDate) return false;
          }
          if (to) {
            const toDate = new Date(to);
            toDate.setHours(23, 59, 59, 999);
            if (cellDate > toDate) return false;
          }
          return true;
        },
        Filter: ({ column }) => <DateRangeFilter column={column} />,
        Cell: ({ row }) =>
          row.original.lastModified
            ? formatedDate(row.original.lastModified)
            : "—",
      },
      {
        accessorKey: "metadata.description",
        header: "Description",
        Cell: ({ row }) => {
          const item = row.original;
          if (item.type !== "file") return <span>—</span>;
          return (
            <ReactQuillComponent value={item.metadata?.description} />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <ActionsCell item={row.original} activeCustomerId={activeCustomerId} />
        ),
      },
    ],
    [search, currentPath, navigateTo, activeCustomerId]
  );

  return (
    <div className="doc-mgmt-table-wrapper">
      <MaterialReactTable
        columns={columns}
        data={items}
        enableTopToolbar={true}
        enableBottomToolbar={false}
        enableColumnActions={false}
        enableSorting={true}
        enableFilters={true}
        enableColumnFilters={true}
        enablePagination={false}
        enableColumnResizing={false}
        enableStickyHeader={true}
        enableRowSelection={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        state={{
          isLoading: loading && items.length === 0,
          showProgressBars: loading && items.length > 0,
        }}
        renderTopToolbarCustomActions={() => (
          <Input
            appearance="outline"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            contentAfter={
              search ? (
                <Dismiss16Regular
                  style={{ cursor: "pointer", color: "#888" }}
                  onClick={() => setSearch("")}
                />
              ) : undefined
            }
            style={{ minWidth: 220 }}
          />
        )}
        renderToolbarInternalActions={({ table }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <MRT_ToggleFiltersButton table={table} />
            <Button
              shape="rounded"
              icon={<FolderAddRegular />}
              onClick={onAddFolder}
            >
              Add Folder
            </Button>
            <Button
              shape="rounded"
              appearance="primary"
              className="asc-button-primary"
              icon={<DocumentAddRegular />}
              onClick={onAddDoc}
            >
              Add Document
            </Button>
          </div>
        )}
        muiTableContainerProps={{
          sx: { maxHeight: "calc(100vh - 340px)", overflow: "auto" },
          onScroll: handleScroll,
        }}
        muiTableHeadCellProps={{
          sx: {
            fontWeight: 600,
            fontSize: "1rem",
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #ddd",
            position: "sticky",
            top: 0,
            zIndex: 1,
          },
        }}
        muiTableBodyRowProps={{
          sx: { "&:hover td": { backgroundColor: "#f5f5f5" } },
        }}
        muiTableBodyCellProps={{ sx: { fontSize: "1rem" } }}
        localization={{ noRecordsToDisplay: fileList.length === 0 ? "There are no active records — click Add Folder or Add Document to create a new file" : "This folder is empty" }}
        initialState={{ density: "compact" }}
      />
    </div>
  );
};

export default DocumentList;
